import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Table } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { dayFormatType, formatDay } from 'common/helpers/day';
import { orderStage, orderSecondaryStage } from '~constants/bookings';
import { useHistory } from 'react-router-dom';
import formatPrice from '~helpers/currency';
import { QUERY_DIFFERENT_STAGE_ORDERS } from './queries';

import generatePath from '~settings/routing';

import { useLazyQuery } from '@apollo/client';

const useStyles = makeStyles(theme => ({
  loading: {
    height: '80vh',
  },

  pendingBoxHint: {
    background: '#FF4D4F',
    ...theme.typography.body5,
    padding: '0px 6px',
    borderRadius: 10,
    color: theme.colors.gray1,
  },

  pendingBoxTitle: {
    ...theme.typography.h5,
    marginRight: 8,
    marginBottom: 17,
    display: 'inline-block',
    color: theme.colors.titleColor,
  },
  row: {
    background: theme.colors.gray1,
    marginTop: 16,
    paddingLeft: 24,
    paddingTop: 16,
    paddingRight: 24,
    '& .ant-table-thead': {
      height: '48px!important',
      '&>tr': {
        '&>th': {
          padding: '13px 12px 13px 12px',
        },
      },
    },
    '& .ant-table-tbody > tr > td': {
      padding: '13px 12px 13px 12px',
    },
    '& .ant-table-header-column': {
      ...theme.typography.body2,
      color: theme.colors.titleColor,
      textTransform: 'capitalize',
    },
    '& .ant-table-row': {
      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  circleIcon: {
    width: 6,
    height: 6,
    background: '#f5222d',
    display: 'inline-block',
    marginRight: 8,
    borderRadius: '50%',
    verticalAlign: 2,
  },
}));

const PendingRefunding = ({ order }) => {
  const classes = useStyles();
  const history = useHistory();
  const [orderInfo, setOrderInfo] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);

  const formatOrder = data => {
    const { totalCount, edges } = data;
    const orders = edges?.map(i => i.node);
    setOrderInfo(orders);
    setTotalCount(totalCount);
  };

  useEffect(() => {
    formatOrder(order);
  }, [order]);

  const { t } = useTranslation();
  const openOrderDetailsPage = record => {
    history.push(generatePath('orders.detail', { id: record?.referenceId }));
  };
  const isShowcounterOffer = order => {
    const isPendingCounterOffer =
      order.secondaryStage === orderSecondaryStage.PENDING_COUNTER_OFFER;

    const isOverTimeCounterOffer =
      order.secondaryStage === orderSecondaryStage.COUNTER_OFFER_NON_CONFIRM_IN_TIME;

    const isRejectAll =
      order.secondaryStage === orderSecondaryStage.REJECT_BY_LANDLORD &&
      order.stage === orderStage.LOST &&
      !!order?.counterOffers?.length;
    return isPendingCounterOffer || isOverTimeCounterOffer || isRejectAll;
  };
  const studentNameField = record => {
    const { passportFirstName, passportMiddleName, passportLastName } = record?.student ?? {};
    let studentName = '-';

    if (passportFirstName) {
      studentName = passportFirstName;
    }

    if (passportMiddleName) {
      studentName =
        studentName === '-' ? passportMiddleName : studentName.concat(' ', passportMiddleName);
    }
    if (passportLastName) {
      studentName =
        studentName === '-' ? passportLastName : studentName.concat(' ', passportLastName);
    }

    return studentName;
  };

  const columns = [
    {
      title: t('landlord.long_tail.booking.booking_number.field'),
      dataIndex: 'id',
      key: 'id',
      width: '20%',
      render: (text, record) => <span>{record?.referenceId || '-'}</span>,
    },
    {
      title: t('landlord.long_tail.booking.student_name.field'),
      dataIndex: 'studentName',
      key: 'studentName',
      width: '23%',
      render: (text, record) => <span>{studentNameField(record)}</span>,
    },
    {
      title: t('landlord.payment.list.column.property_name'),
      dataIndex: 'propertyName',
      key: 'propertyName',
      width: '22%',
      render: (text, record) => <span>{record?.property ? `${record.property.name} ` : '-'}</span>,
    },

    {
      title: t('landlord.long_tail.booking.move_in_date.field'),
      dataIndex: 'MoveInDate',
      key: 'MoveInDate',
      width: '18%',
      render: (text, row) => {
        let moveInDate = isShowcounterOffer(row)
          ? row.counterOffers?.[0]?.moveInDate
          : row?.moveInDate;

        return moveInDate ? formatDay(dayFormatType.CONTENTDATE, moveInDate) : '-';
      },
    },
    {
      title: t('landlord.long_tail.booking.total_price.field'),
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: '18%',
      render: (text, row) => {
        const record = isShowcounterOffer(row) ? row.counterOffers?.[0] : row;

        return formatPrice(record?.totalPrice, record?.currency);
      },
    },
  ];

  const [queryOrder, { data, loading }] = useLazyQuery(QUERY_DIFFERENT_STAGE_ORDERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
    onCompleted: result => {
      if (result?.viewer?.company?.orders) {
        formatOrder(result.viewer.company.orders);
      }
    },
  });

  const getOrder = pageInfo => {
    const { current, pageSize } = pageInfo;

    const variables = {
      pageNumber: current,
      stages: 'REFUNDING',
      pageSize,
    };

    setPageSize(pageSize);
    setCurrent(current);

    queryOrder({ variables });
  };

  const handleShowSizeChange = (_, size) => {
    getOrder({
      current: 1,
      pageSize: size,
    });
  };

  return (
    <Row className={classes.row}>
      <Col span={24}>
        <span theme="filled" className={classes.circleIcon} />

        <span className={classes.pendingBoxTitle}>{t('board.pending_refunding.title')}</span>
      </Col>

      <If condition={totalCount}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={orderInfo}
          loading={loading}
          onChange={getOrder}
          pagination={{
            current,
            pageSize,
            showSizeChanger: true,
            hideOnSinglePage: false,
            total: totalCount,
            onShowSizeChange: handleShowSizeChange,
          }}
          onRow={record => {
            return {
              onClick: () => {
                openOrderDetailsPage(record);
              },
            };
          }}
        />
      </If>
    </Row>
  );
};

export default PendingRefunding;
